import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { createSupportChatSession, sendSupportChatMessage, getSupportChatHistory, sendSupportChatMessageStream } from '../../services/supportChatService'

const createMessage = (role, content, metadata = {}) => ({
  id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `msg-${Date.now()}-${Math.random()}`,
  role,
  content,
  createdAt: new Date().toISOString(),
  metadata,
})

const initialState = {
  sessionId: null,
  isOpen: false,
  messages: [],
  status: 'idle',
  sending: false,
  error: null,
  unreadCount: 0,
  welcomeSent: false,
  historyLoaded: false,
  hasMore: false,
  totalMessages: 0,
  streamingMessageId: null,
  streamingContent: '',
}

export const initializeSupportChat = createAsyncThunk(
  'supportChat/initialize',
  async (options = {}, { rejectWithValue }) => {
    try {
      const session = await createSupportChatSession(options)
      return session
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to start support chat.')
    }
  },
)

export const sendSupportChatMessageThunk = createAsyncThunk(
  'supportChat/sendMessage',
  async ({ content }, { getState, rejectWithValue }) => {
    try {
      const { supportChat } = getState()
      const payload = await sendSupportChatMessage({
        sessionId: supportChat.sessionId,
        content,
      })
      return payload
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to send support message.')
    }
  },
)

export const loadSupportChatHistory = createAsyncThunk(
  'supportChat/loadHistory',
  async (limit = 50, { rejectWithValue }) => {
    try {
      const response = await getSupportChatHistory(limit)
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load chat history.')
    }
  },
)

const supportChatSlice = createSlice({
  name: 'supportChat',
  initialState,
  reducers: {
    openSupportChat: (state) => {
      state.isOpen = true
      state.unreadCount = 0
    },
    closeSupportChat: (state) => {
      state.isOpen = false
    },
    enqueueSupportChatMessage: (state, action) => {
      state.messages.push(action.payload)
      if (!state.isOpen && action.payload.role === 'assistant') {
        state.unreadCount += 1
      }
    },
    markWelcomeSent: (state) => {
      state.welcomeSent = true
    },
    resetSupportChat: () => {
      return initialState
    },
    startStreaming: (state, action) => {
      state.sending = true
      state.streamingMessageId = action.payload.id
      state.streamingContent = ''
      state.error = null
    },
    appendStreamingContent: (state, action) => {
      state.streamingContent += action.payload
    },
    completeStreaming: (state) => {
      if (state.streamingMessageId && state.streamingContent) {
        state.messages.push({
          id: state.streamingMessageId,
          role: 'assistant',
          content: state.streamingContent,
          createdAt: new Date().toISOString(),
          metadata: {},
        })
        if (!state.isOpen) {
          state.unreadCount += 1
        }
      }
      state.sending = false
      state.streamingMessageId = null
      state.streamingContent = ''
    },
    cancelStreaming: (state) => {
      state.sending = false
      state.streamingMessageId = null
      state.streamingContent = ''
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeSupportChat.pending, (state) => {
        state.status = 'connecting'
        state.error = null
      })
      .addCase(initializeSupportChat.fulfilled, (state, action) => {
        state.status = 'ready'
        state.sessionId = action.payload?.sessionId || state.sessionId
        state.error = null
      })
      .addCase(initializeSupportChat.rejected, (state, action) => {
        state.status = 'error'
        state.error = action.payload || action.error.message
      })
      .addCase(sendSupportChatMessageThunk.pending, (state) => {
        state.sending = true
        state.error = null
      })
      .addCase(sendSupportChatMessageThunk.fulfilled, (state, action) => {
        state.sending = false
        const { messages = [] } = action.payload || {}
        messages.forEach((msg) => {
          state.messages.push({
            id: msg.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `msg-${Date.now()}-${Math.random()}`),
            role: msg.role || 'assistant',
            content: msg.content,
            createdAt: msg.createdAt || new Date().toISOString(),
            metadata: msg.metadata || {},
          })
        })
        if (!state.isOpen && messages.some((msg) => (msg.role || 'assistant') === 'assistant')) {
          state.unreadCount += 1
        }
      })
      .addCase(sendSupportChatMessageThunk.rejected, (state, action) => {
        state.sending = false
        state.error = action.payload || action.error.message
        state.messages.push(
          createMessage(
            'assistant',
            'We could not process that request right now. Please try again or send us an email at support@quantumhusky.ai.',
            { error: true },
          ),
        )
        if (!state.isOpen) {
          state.unreadCount += 1
        }
      })
      .addCase(loadSupportChatHistory.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(loadSupportChatHistory.fulfilled, (state, action) => {
        state.status = 'ready'
        state.historyLoaded = true
        const { messages = [], sessionId, hasMore = false, totalMessages = 0 } = action.payload || {}
        if (sessionId) {
          state.sessionId = sessionId
        }
        state.hasMore = hasMore
        state.totalMessages = totalMessages
        if (messages.length > 0) {
          state.messages = messages.map((msg) => ({
            id: msg.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `msg-${Date.now()}-${Math.random()}`),
            role: msg.role || 'assistant',
            content: msg.content,
            createdAt: msg.createdAt || new Date().toISOString(),
            metadata: msg.metadata || {},
          }))
          state.welcomeSent = true
        }
      })
      .addCase(loadSupportChatHistory.rejected, (state, action) => {
        state.status = 'ready'
        state.historyLoaded = true
        state.error = action.payload || action.error.message
      })
  },
})

export const {
  openSupportChat,
  closeSupportChat,
  enqueueSupportChatMessage,
  markWelcomeSent,
  resetSupportChat,
  startStreaming,
  appendStreamingContent,
  completeStreaming,
  cancelStreaming,
} = supportChatSlice.actions

export { createMessage }

export default supportChatSlice.reducer
